<template>
  <div class="template-connected">
    <div id="container">
      <div v-if="userConnected.roles && userConnected.roles.includes('ROLE_SUPER_ADMIN')" class="block-flat">
        <div class="block-content">
          <fieldset>
            <legend>
              <b-row>
                <b-col cols="6">Gestion du mode maintenance</b-col>
                <b-col cols="6" class="text-right">
                  <b-button variant="outline-secondary" size="sm" @click="refreshAll" class="mb-2">
                    <font-awesome-icon icon="sync" class="mr-1"></font-awesome-icon>
                    Actualiser
                  </b-button>
                </b-col>
              </b-row>
            </legend>
          </fieldset>

          <b-alert v-if="status.active" show variant="warning" class="mb-3">
            Le mode maintenance est actif jusqu’au
            <strong>{{ formatDisplay(status.current_window && status.current_window.ends_at) }}</strong>.
          </b-alert>

          <b-card
            class="mb-3 mt-4 maintenance-mode-encart"
            border-variant="secondary"
            header="À propos du mode maintenance"
            header-bg-variant="light"
            header-text-variant="dark"
            header-class="font-weight-bold py-2"
          >
            <b-card-text class="mb-0">
              <ul class="mb-0 pl-3">
                <li>
                  Les dépôts et mises à jour de demandes, ainsi que la création de compte, sont bloqués.
                </li>
                <li>
                  Une page informant les utilisateurs d’une maintenance en cours s’affiche d’office,
                  quelle que soit la route front appelée.
                </li>
                <li>
                  Les utilisateurs disposant du rôle super-administrateur peuvent gérer le mode maintenance.
                </li>
              </ul>
            </b-card-text>
          </b-card>

          <h2 class="h5 mt-4">Planifier une fenêtre</h2>
          <b-form @submit.prevent="submitNewWindow" class="mb-4">
            <b-row>
              <b-col md="4">
                <b-form-group label="Début" label-for="mw-start">
                  <b-form-input
                    id="mw-start"
                    v-model="form.startsLocal"
                    type="datetime-local"
                    required
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Fin" label-for="mw-end">
                  <b-form-input
                    id="mw-end"
                    v-model="form.endsLocal"
                    type="datetime-local"
                    required
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="4" class="d-flex align-items-end">
                <b-button type="submit" variant="success" :disabled="saving" size="sm" class="mb-4">
                  <font-awesome-icon icon="plus" class="mr-2"></font-awesome-icon>
                  Enregistrer
                </b-button>
              </b-col>
            </b-row>
          </b-form>

          <h2 class="h5">Périodes enregistrées</h2>
          <b-table
            :items="windows"
            :fields="tableFields"
            striped
            hover
            responsive
            :busy="loading"
            empty-text="Aucune fenêtre"
            show-empty
          >
            <template #table-busy>
              <div class="text-center text-muted my-3">Chargement…</div>
            </template>
            <template #cell(starts_at)="row">
              {{ formatDisplay(row.item.starts_at) }}
            </template>
            <template #cell(ends_at)="row">
              {{ formatDisplay(row.item.ends_at) }}
            </template>
            <template #cell(status)="row">
              <b-badge :variant="statusVariant(row.item.status)">
                {{ statusLabel(row.item.status) }}
              </b-badge>
            </template>
            <template #cell(actions)="row">
              <span v-if="row.item.status === 'current'">
                <b-button size="sm" variant="danger" class="mr-1" @click="endNow(row.item)">
                  Terminer
                </b-button>
                <b-button size="sm" variant="primary" @click="openExtend(row.item)">
                  Prolonger
                </b-button>
              </span>
              <span v-else-if="row.item.status === 'future'">
                <b-button size="sm" variant="outline-danger" @click="cancelFuture(row.item)">
                  Annuler
                </b-button>
              </span>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>
        </div>
      </div>
      <div v-else class="block-flat">
        <div class="block-content">
          <b-alert show variant="danger">Accès réservé aux super-administrateurs.</b-alert>
        </div>
      </div>

      <b-modal
        v-model="extendModal.show"
        title="Prolonger la maintenance"
        @ok="confirmExtend"
        ok-title="Valider"
        cancel-title="Annuler"
      >
        <b-form-group label="Nouvelle date et heure de fin">
          <b-form-input v-model="extendModal.endsLocal" type="datetime-local"></b-form-input>
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<script>
import Vuex from "vuex";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

export default {
  name: "MaintenanceManagement",
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        startsLocal: "",
        endsLocal: "",
      },
      extendModal: {
        show: false,
        id: null,
        endsLocal: "",
      },
      tableFields: [
        { key: "starts_at", label: "Début" },
        { key: "ends_at", label: "Fin" },
        { key: "status", label: "Statut" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    ...Vuex.mapGetters("Authentification", ["userConnected"]),
    ...Vuex.mapGetters("Maintenance", ["windows"]),
    status() {
      return this.$store.state.Maintenance.status;
    },
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    ...Vuex.mapActions("Maintenance", [
      "fetchStatus",
      "fetchWindows",
      "createWindow",
      "endWindowNow",
      "extendWindow",
      "cancelFutureWindow",
    ]),
    parseMaintenanceInstant(value) {
      if (value == null || value === "") {
        return null;
      }
      const s = String(value).trim();
      let m = moment(s, moment.ISO_8601, true);
      if (m.isValid()) {
        return m;
      }
      m = moment(s, "YYYY/MM/DD [à] HH:mm:ss", true);
      if (m.isValid()) {
        return m;
      }
      m = moment(s);
      return m.isValid() ? m : null;
    },
    toIsoFromLocal(localStr) {
      if (!localStr) {
        return null;
      }
      const m = moment(localStr, ["YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss"], true);
      if (!m.isValid()) {
        return null;
      }
      return m.toISOString();
    },
    formatDisplay(value) {
      const m = this.parseMaintenanceInstant(value);
      if (!m) {
        return value ? String(value) : "—";
      }
      return m.format("DD/MM/YYYY HH:mm");
    },
    statusLabel(s) {
      if (s === "current") {
        return "En cours";
      }
      if (s === "future") {
        return "À venir";
      }
      return "Terminée";
    },
    statusVariant(s) {
      if (s === "current") {
        return "warning";
      }
      if (s === "future") {
        return "info";
      }
      return "secondary";
    },
    async refreshAll() {
      this.loading = true;
      try {
        await this.fetchStatus();
        await this.fetchWindows();
      } catch (e) {
        this.$awn.alert("Impossible de charger les données de maintenance.");
      } finally {
        this.loading = false;
      }
    },
    async submitNewWindow() {
      const starts_at = this.toIsoFromLocal(this.form.startsLocal);
      const ends_at = this.toIsoFromLocal(this.form.endsLocal);
      if (!starts_at || !ends_at) {
        this.$awn.alert("Dates invalides.");
        return;
      }
      const mStart = moment(starts_at);
      const mEnd = moment(ends_at);
      const mNow = moment();
      if (!mStart.isAfter(mNow)) {
        this.$awn.alert("La date et l’heure de début doivent être dans le futur.");
        return;
      }
      if (!mEnd.isAfter(mNow)) {
        this.$awn.alert("La date et l’heure de fin doivent être dans le futur.");
        return;
      }
      if (!mEnd.isAfter(mStart)) {
        this.$awn.alert("La date de fin doit être postérieure à la date de début.");
        return;
      }
      this.saving = true;
      try {
        const res = await this.createWindow({ starts_at, ends_at });
        if (res.data && res.data.success) {
          this.$awn.success(res.data.message || "Enregistré.");
          this.form.startsLocal = "";
          this.form.endsLocal = "";
          await this.refreshAll();
        } else {
          this.$awn.alert((res.data && res.data.message) || "Erreur à l’enregistrement.");
        }
      } catch (e) {
        const msg =
          e.response &&
          e.response.data &&
          e.response.data.message;
        this.$awn.alert(msg || "Erreur à l’enregistrement.");
      } finally {
        this.saving = false;
      }
    },
    cancelFuture(item) {
      this.$awn.confirm(
        "Annuler cette fenêtre de maintenance planifiée ? Elle sera supprimée.",
        async () => {
          try {
            const res = await this.cancelFutureWindow(item.id);
            if (res.data && res.data.success) {
              this.$awn.success(res.data.message || "Fenêtre annulée.");
              await this.refreshAll();
            }
          } catch (e) {
            const msg =
              e.response &&
              e.response.data &&
              e.response.data.message;
            this.$awn.alert(msg || "Erreur.");
          }
        },
        () => {}
      );
    },
    endNow(item) {
      this.$awn.confirm(
        "Mettre fin immédiatement à cette période de maintenance ?",
        async () => {
          try {
            const res = await this.endWindowNow(item.id);
            if (res.data && res.data.success) {
              this.$awn.success(res.data.message || "Mis à jour.");
              await this.refreshAll();
            }
          } catch (e) {
            const msg =
              e.response &&
              e.response.data &&
              e.response.data.message;
            this.$awn.alert(msg || "Erreur.");
          }
        },
        () => {}
      );
    },
    openExtend(item) {
      this.extendModal.id = item.id;
      this.extendModal.endsLocal = this.isoToDatetimeLocal(item.ends_at);
      this.extendModal.show = true;
    },
    isoToDatetimeLocal(iso) {
      const m = this.parseMaintenanceInstant(iso);
      if (!m) {
        return "";
      }
      return m.format("YYYY-MM-DDTHH:mm");
    },
    async confirmExtend(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
        bvModalEvt.preventDefault();
      }
      const ends_at = this.toIsoFromLocal(this.extendModal.endsLocal);
      if (!ends_at) {
        this.$awn.alert("Date de fin invalide.");
        return;
      }
      try {
        const res = await this.extendWindow({
          id: this.extendModal.id,
          ends_at,
        });
        if (res.data && res.data.success) {
          this.$awn.success(res.data.message || "Prolongation enregistrée.");
          this.extendModal.show = false;
          await this.refreshAll();
        }
      } catch (e) {
        const msg =
          e.response &&
          e.response.data &&
          e.response.data.message;
        this.$awn.alert(msg || "Erreur.");
      }
    },
  },
};
</script>

<style scoped>
.maintenance-mode-encart {
  background-color: #f8f9fa;
}
</style>
